/**
 * fv.ts — Categoría "Frutas y Verduras" (10 productos demo).
 * Insumos frescos para cocina, ensaladas y preparaciones del día.
 */
import { buildProduct } from "./_factory";
import type { Producto } from "@/types";

const CAT = {
  categoryId: "cat_fv",
  skuPrefix: "FV",
  categoria: "Frutas y Verduras",
} as const;

export const cat_fv_products: Producto[] = [
  buildProduct({
    ...CAT,
    index: 0,
    nombre: "Tomate Chonto 1kg",
    marca: "Campo Fresco",
    origen: "Colombia",
    costo: 2200,
    precio: 3500,
    stock: 140,
    stockMinimo: 30,
    stockMaximo: 250,
    unidad: "kg",
    ubicacionFisica: "Nevera verduras 1",
    proveedor: "Corabastos Distribuidor",
    tiempoEntrega: "Diario",
    vencimientoMeses: 1,
    pesoNeto: "1kg",
    ventaPorPeso: true,
    descripcionLarga:
      "Tomate chonto maduro, perfecto para guisar, ensaladas y salsas. Piel firme, pulpa jugosa. Cosecha rotativa que garantiza punto óptimo de maduración.",
    especificaciones: [
      { clave: "Variedad", valor: "Chonto" },
      { clave: "Maduración", valor: "Maduro listo" },
    ],
    imageKeywords: ["tomato", "tomate", "vegetable"],
    color: "bg-red-500",
    shape: "circle",
  }),
  buildProduct({
    ...CAT,
    index: 1,
    nombre: "Cebolla Cabezona 1kg",
    marca: "Campo Fresco",
    origen: "Colombia",
    costo: 2600,
    precio: 4200,
    stock: 130,
    stockMinimo: 28,
    stockMaximo: 240,
    unidad: "kg",
    ubicacionFisica: "Estante V1, tubérculos",
    proveedor: "Corabastos Distribuidor",
    vencimientoMeses: 2,
    pesoNeto: "1kg",
    ventaPorPeso: true,
    descripcionLarga:
      "Cebolla cabezona blanca, base aromática de todas las preparaciones saladas. Bulbo firme, piel seca, mínimo desperdicio al pelar.",
    especificaciones: [
      { clave: "Variedad", valor: "Blanca" },
      { clave: "Tamaño", valor: "Mediana-grande" },
    ],
    imageKeywords: ["onion", "cebolla", "white-onion"],
    color: "bg-amber-100",
    shape: "circle",
  }),
  buildProduct({
    ...CAT,
    index: 2,
    nombre: "Lechuga Crespa Verde",
    marca: "Hortícola del Campo",
    origen: "Colombia",
    costo: 1800,
    precio: 2900,
    stock: 80,
    stockMinimo: 20,
    stockMaximo: 150,
    unidad: "unidad",
    ubicacionFisica: "Nevera verduras 1",
    proveedor: "Hortícola del Campo S.A.S.",
    vencimientoMeses: 1,
    pesoNeto: "350g",
    descripcionLarga:
      "Lechuga crespa verde hidropónica, hojas crujientes y frescas. Empacada al vacío el mismo día de cosecha. Ideal para ensaladas y wraps.",
    especificaciones: [
      { clave: "Variedad", valor: "Crespa verde" },
      { clave: "Cultivo", valor: "Hidropónico" },
    ],
    imageKeywords: ["lettuce", "lechuga", "green-leaves"],
    color: "bg-green-400",
    shape: "circle",
  }),
  buildProduct({
    ...CAT,
    index: 3,
    nombre: "Papa Pastusa 1kg",
    marca: "Campo Fresco",
    origen: "Colombia",
    costo: 2400,
    precio: 3900,
    stock: 180,
    stockMinimo: 40,
    stockMaximo: 320,
    unidad: "kg",
    ubicacionFisica: "Estante V1, tubérculos",
    proveedor: "Fedepapa Colombia",
    vencimientoMeses: 2,
    pesoNeto: "1kg",
    ventaPorPeso: true,
    descripcionLarga:
      "Papa pastusa de altura, ideal para sopas, purés y frituras. Cáscara rugosa, pulpa amarilla suave que se deshace al cocinar.",
    especificaciones: [
      { clave: "Variedad", valor: "Pastusa suprema" },
      { clave: "Uso", valor: "Sopa / puré" },
    ],
    imageKeywords: ["potato", "papa", "potatoes"],
    color: "bg-amber-300",
    shape: "circle",
  }),
  buildProduct({
    ...CAT,
    index: 4,
    nombre: "Aguacate Hass",
    marca: "Campo Fresco",
    origen: "Colombia",
    costo: 3500,
    precio: 5500,
    stock: 100,
    stockMinimo: 22,
    stockMaximo: 180,
    unidad: "unidad",
    ubicacionFisica: "Nevera frutas 2",
    proveedor: "Aguacateros del Quindío",
    vencimientoMeses: 1,
    pesoNeto: "220g",
    descripcionLarga:
      "Aguacate Hass de exportación, maduración controlada. Pulpa cremosa, sabor intenso. Perfecto para guacamole, ensaladas y desayunos.",
    especificaciones: [
      { clave: "Variedad", valor: "Hass" },
      { clave: "Tamaño", valor: "Mediano" },
    ],
    imageKeywords: ["avocado", "aguacate", "hass"],
    color: "bg-green-700",
    shape: "circle",
  }),
  buildProduct({
    ...CAT,
    index: 5,
    nombre: "Plátano Verde",
    marca: "Campo Fresco",
    origen: "Colombia",
    costo: 1200,
    precio: 1900,
    stock: 160,
    stockMinimo: 35,
    stockMaximo: 280,
    unidad: "unidad",
    ubicacionFisica: "Estante V2, plátanos",
    proveedor: "Frutera del Pacífico",
    vencimientoMeses: 1,
    pesoNeto: "250g",
    descripcionLarga:
      "Plátano verde hartón, grande y firme. Base indispensable para patacón, sancocho y tajadas. Maduración controlada para usos salados.",
    especificaciones: [
      { clave: "Variedad", valor: "Hartón verde" },
      { clave: "Uso", valor: "Patacón / sopa" },
    ],
    imageKeywords: ["green-plantain", "platano", "banana"],
    color: "bg-green-500",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 6,
    nombre: "Zanahoria 1kg",
    marca: "Campo Fresco",
    origen: "Colombia",
    costo: 1900,
    precio: 3100,
    stock: 120,
    stockMinimo: 25,
    stockMaximo: 220,
    unidad: "kg",
    ubicacionFisica: "Nevera verduras 1",
    proveedor: "Corabastos Distribuidor",
    vencimientoMeses: 1,
    pesoNeto: "1kg",
    ventaPorPeso: true,
    descripcionLarga:
      "Zanahoria fresca de huerta, dulce y crujiente. Lavada, lista para preparar. Excelente fuente de betacaroteno y fibra.",
    especificaciones: [
      { clave: "Calibre", valor: "Medio" },
      { clave: "Procedencia", valor: "Cundinamarca" },
    ],
    imageKeywords: ["carrot", "zanahoria", "orange-vegetable"],
    color: "bg-orange-500",
    shape: "diamond",
  }),
  buildProduct({
    ...CAT,
    index: 7,
    nombre: "Limón Tahití 1kg",
    marca: "Campo Fresco",
    origen: "Colombia",
    costo: 3200,
    precio: 4900,
    stock: 90,
    stockMinimo: 20,
    stockMaximo: 170,
    unidad: "kg",
    ubicacionFisica: "Nevera frutas 2",
    proveedor: "Frutera del Pacífico",
    vencimientoMeses: 1,
    pesoNeto: "1kg",
    ventaPorPeso: true,
    descripcionLarga:
      "Limón Tahití de cáscara delgada y jugoso. Ideal para limonadas, aderezos, pescados y cocteles. Calidad exportación.",
    especificaciones: [
      { clave: "Variedad", valor: "Tahití" },
      { clave: "Jugosidad", valor: "Alta" },
    ],
    imageKeywords: ["lime", "limon", "citrus"],
    color: "bg-lime-500",
    shape: "circle",
  }),
  buildProduct({
    ...CAT,
    index: 8,
    nombre: "Banano Común 1kg",
    marca: "Campo Fresco",
    origen: "Colombia",
    costo: 1600,
    precio: 2600,
    stock: 150,
    stockMinimo: 35,
    stockMaximo: 280,
    unidad: "kg",
    ubicacionFisica: "Estante V2, frutas",
    proveedor: "Augura Colombia",
    vencimientoMeses: 1,
    pesoNeto: "1kg",
    ventaPorPeso: true,
    descripcionLarga:
      "Banano común maduro al punto, dulce y natural. Excelente fuente de potasio. Ideal para batidos, postres y refrigerios.",
    especificaciones: [
      { clave: "Variedad", valor: "Cavendish" },
      { clave: "Maduración", valor: "Lista para consumir" },
    ],
    imageKeywords: ["banana", "banano", "yellow-fruit"],
    color: "bg-yellow-400",
    shape: "square",
  }),
  buildProduct({
    ...CAT,
    index: 9,
    nombre: "Fresa Fresca 500g",
    marca: "Hortícola del Campo",
    origen: "Colombia",
    costo: 4800,
    precio: 7500,
    stock: 70,
    stockMinimo: 15,
    stockMaximo: 140,
    unidad: "caja",
    ubicacionFisica: "Nevera frutas 2",
    proveedor: "Hortícola del Campo S.A.S.",
    vencimientoMeses: 1,
    pesoNeto: "500g",
    descripcionLarga:
      "Fresa fresca roja, seleccionada a mano. Empaque ventilado que conserva frescura por más tiempo. Perfecta para postres, jugos y decoración.",
    especificaciones: [
      { clave: "Calibre", valor: "Grande" },
      { clave: "Cosecha", valor: "24h" },
    ],
    imageKeywords: ["strawberry", "fresa", "red-berry"],
    color: "bg-red-400",
    shape: "circle",
  }),
];
